import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Linkedin, Instagram, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import axios from 'axios';
import { API } from '../lib/api';
import { useSession } from './header/useSession';

const SocialLink = ({ href, Icon, testid }) => {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noreferrer"
      className="p-2 bg-gray-700 hover:bg-[#ee5e23] rounded-full transition-colors"
      data-testid={testid}>
      <Icon className="w-4 h-4" />
    </a>
  );
};

const Footer = () => {
  const [pages, setPages] = useState([]);
  const [cfg, setCfg] = useState({});
  const [session] = useSession();
  const isVendor = session?.role === 'vendor';

  useEffect(() => {
    axios.get(`${API}/public-pages`).then((r) => setPages(r.data || [])).catch(() => setPages([]));
    axios.get(`${API}/footer-settings`).then((r) => setCfg(r.data || {})).catch(() => setCfg({}));
  }, []);

  const pageBySlug = (slug) => pages.find((p) => p.slug === slug);
  const link = (slug, fallback) => {
    const p = pageBySlug(slug);
    return p ? <Link to={`/p/${p.slug}`} className="hover:text-white transition-colors" data-testid={`footer-link-${p.slug}`}>{p.title}</Link>
             : <span className="text-gray-500 cursor-not-allowed">{fallback}</span>;
  };

  const wellKnown = new Set(['about-us', 'contact-us', 'privacy-policy', 'terms-and-conditions', 'refund-policy']);
  const extraPages = pages.filter((p) => !wellKnown.has(p.slug));

  const col1 = cfg.column1_title || 'TelanganaMart';
  const col2 = cfg.column2_title || 'Help';
  const col3 = cfg.column3_title || 'For Vendors';

  return (
    <footer className="bg-[#1e293b] text-gray-300 mt-12">
      <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div>
          <h4 className="text-white font-semibold mb-4">{col1}</h4>
          <ul className="space-y-2 text-sm">
            <li>{link('about-us', 'About Us')}</li>
            <li>{link('contact-us', 'Contact Us')}</li>
            {extraPages.map((p) => (
              <li key={p.slug}>
                <Link to={`/p/${p.slug}`} className="hover:text-white transition-colors" data-testid={`footer-link-${p.slug}`}>{p.title}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">{col2}</h4>
          <ul className="space-y-2 text-sm">
            <li>{link('contact-us', 'Customer Care')}</li>
            <li>{link('privacy-policy', 'Privacy Policy')}</li>
            <li>{link('terms-and-conditions', 'Terms & Conditions')}</li>
            <li>{link('refund-policy', 'Refund Policy')}</li>
            <li><Link to="/delete-account" className="hover:text-white transition-colors" data-testid="footer-delete-account">Delete Account</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">{col3}</h4>
          <ul className="space-y-2 text-sm">
            {!isVendor && <li><Link to="/vendor/register" className="hover:text-white transition-colors">Register as Vendor</Link></li>}
            {!isVendor && <li><Link to="/vendor/register" className="hover:text-white transition-colors">Sell on TelanganaMart</Link></li>}
            <li><Link to="/directory" className="hover:text-white transition-colors">Business Directory</Link></li>
            <li><Link to="/vendor/login" className="hover:text-white transition-colors">Vendor Login</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Contact Us</h4>
          <ul className="space-y-3 text-sm">
            {cfg.contact_phone && <li className="flex items-center gap-2"><Phone className="w-4 h-4 shrink-0" /> <a href={`tel:${cfg.contact_phone}`} className="hover:text-white">{cfg.contact_phone}</a></li>}
            {cfg.contact_email && <li className="flex items-center gap-2"><Mail className="w-4 h-4 shrink-0" /> <a href={`mailto:${cfg.contact_email}`} className="hover:text-white break-all">{cfg.contact_email}</a></li>}
            {cfg.contact_address && <li className="flex items-start gap-2"><MapPin className="w-4 h-4 shrink-0 mt-0.5" /> <span>{cfg.contact_address}</span></li>}
          </ul>
          <div className="flex gap-3 mt-4 flex-wrap">
            <SocialLink href={cfg.social_facebook} Icon={Facebook} testid="footer-social-facebook" />
            <SocialLink href={cfg.social_twitter} Icon={Twitter} testid="footer-social-twitter" />
            <SocialLink href={cfg.social_linkedin} Icon={Linkedin} testid="footer-social-linkedin" />
            <SocialLink href={cfg.social_instagram} Icon={Instagram} testid="footer-social-instagram" />
            <SocialLink href={cfg.social_youtube} Icon={Youtube} testid="footer-social-youtube" />
          </div>
        </div>
      </div>
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row justify-between items-center text-xs text-gray-400">
          <p>{cfg.copyright_text || `© ${new Date().getFullYear()} TelanganaMart B2B Marketplace. All rights reserved.`}</p>
          {cfg.tagline && <p className="mt-2 md:mt-0">{cfg.tagline}</p>}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
